import {
  TriggerCallRouting,
  TriggerCallType,
  TriggerEventType,
  TriggerTargetType,
  type CaraWorkspacePreset,
  type CaraWorkspaceUseCase,
  type CreateTriggerDefinitionInput,
  type TriggerDefinitionFieldsFragment,
  type TriggerEventMatcherInput,
  type TriggerTargetInput
} from '~/api/graphql';
import { browserTimezone } from '~/lib/agency-timezones';
import {
  cronToSchedule,
  normalizeEmailSuffix,
  ScheduleWeekday,
  scheduleToCron
} from './trigger-utils';

/**
 * The preset a trigger launches. A built-in carries its preset as well as its use
 * case so an API-authored target the catalog does not list still round-trips.
 */
export type TriggerTask =
  | { kind: 'custom'; presetId: string }
  | { kind: 'builtIn'; preset: CaraWorkspacePreset; useCase: CaraWorkspaceUseCase | null };

/** Fields the edit UI owns. Opaque matcher slots are passed through from `existing`. */
export type TriggerFormState = {
  name: string;
  /** Null until the user picks a trigger type. */
  eventType: TriggerEventType | null;
  emailSuffix: string;
  scheduleTime: string;
  scheduleDays: ScheduleWeekday[];
  scheduleTimezone: string;
  /**
   * Cron that is not weekday+time shaped. Kept so update does not rewrite an
   * API-authored schedule into an empty/invalid expression.
   */
  scheduleCronPassthrough: string | null;
  callType: TriggerCallType;
  callRouting: TriggerCallRouting;
  callMinimumDurationSeconds: number;
  task: TriggerTask | null;
  prompt: string;
};

export const emptyTriggerForm = (): TriggerFormState => ({
  name: '',
  eventType: null,
  emailSuffix: '',
  scheduleTime: '09:00',
  scheduleDays: [
    ScheduleWeekday.Mon,
    ScheduleWeekday.Tue,
    ScheduleWeekday.Wed,
    ScheduleWeekday.Thu,
    ScheduleWeekday.Fri
  ],
  scheduleTimezone: browserTimezone(),
  scheduleCronPassthrough: null,
  callType: TriggerCallType.Any,
  callRouting: TriggerCallRouting.AnyAccount,
  callMinimumDurationSeconds: 0,
  task: null,
  prompt: ''
});

export const taskFromTarget = (
  target: TriggerDefinitionFieldsFragment['target']
): TriggerTask | null => {
  if (target.preset) {
    return { kind: 'builtIn', preset: target.preset, useCase: target.useCase ?? null };
  }
  return target.presetId ? { kind: 'custom', presetId: target.presetId } : null;
};

const targetFromTask = (task: TriggerTask | null): Partial<TriggerTargetInput> => {
  if (task?.kind === 'custom') return { presetId: task.presetId };
  if (task?.kind === 'builtIn') return { preset: task.preset, useCase: task.useCase ?? undefined };
  return {};
};

export const formFromTrigger = (trigger: TriggerDefinitionFieldsFragment): TriggerFormState => {
  const matcher = trigger.eventMatcher;
  const parsedSchedule = matcher.scheduleConfig
    ? cronToSchedule(matcher.scheduleConfig.cron)
    : null;
  return {
    name: trigger.name,
    eventType: matcher.eventType,
    emailSuffix: matcher.caraAddressConfig?.emailSuffix ?? '',
    scheduleTime: parsedSchedule?.time ?? '09:00',
    scheduleDays: parsedSchedule?.days ?? [],
    scheduleTimezone: matcher.scheduleConfig?.timezone ?? browserTimezone(),
    scheduleCronPassthrough:
      matcher.scheduleConfig && !parsedSchedule ? matcher.scheduleConfig.cron : null,
    callType: matcher.callAnalyzedConfig?.callType ?? TriggerCallType.Any,
    callRouting: matcher.callAnalyzedConfig?.routing ?? TriggerCallRouting.AnyAccount,
    callMinimumDurationSeconds: matcher.callAnalyzedConfig?.minimumDurationSeconds ?? 0,
    task: taskFromTarget(trigger.target),
    prompt: trigger.target.prompt ?? ''
  };
};

export const buildEventMatcher = (
  form: TriggerFormState,
  existing?: TriggerDefinitionFieldsFragment | null
): TriggerEventMatcherInput => {
  const prior = existing?.eventMatcher;

  if (form.eventType === TriggerEventType.EmailReceived) {
    // Mailbox sources are API/ops-authored for now; round-trip the full config.
    if (prior?.eventType === TriggerEventType.EmailReceived && prior.mailboxConfig) {
      return {
        eventType: TriggerEventType.EmailReceived,
        mailboxConfig: {
          integrationId: prior.mailboxConfig.integrationId,
          folder: prior.mailboxConfig.folder ?? undefined,
          subjectContains: prior.mailboxConfig.subjectContains ?? undefined,
          senderDomain: prior.mailboxConfig.senderDomain ?? undefined,
          hasAttachments: prior.mailboxConfig.hasAttachments
        }
      };
    }
    const priorCara =
      prior?.eventType === TriggerEventType.EmailReceived ? prior.caraAddressConfig : null;
    return {
      eventType: TriggerEventType.EmailReceived,
      caraAddressConfig: {
        emailSuffix: normalizeEmailSuffix(form.emailSuffix),
        subjectContains: priorCara?.subjectContains ?? undefined,
        senderDomain: priorCara?.senderDomain ?? undefined,
        hasAttachments: priorCara?.hasAttachments ?? false
      }
    };
  }

  if (form.eventType === TriggerEventType.ScheduleDue) {
    const priorSchedule =
      prior?.eventType === TriggerEventType.ScheduleDue ? prior.scheduleConfig : null;
    return {
      eventType: TriggerEventType.ScheduleDue,
      scheduleConfig: {
        cron: form.scheduleCronPassthrough ?? scheduleToCron(form.scheduleTime, form.scheduleDays),
        timezone: form.scheduleTimezone,
        catchUpHours: priorSchedule?.catchUpHours ?? 24
      }
    };
  }

  return {
    eventType: TriggerEventType.CallAnalyzed,
    callAnalyzedConfig: {
      callType: form.callType,
      routing: form.callRouting,
      minimumDurationSeconds: form.callMinimumDurationSeconds
    }
  };
};

export const buildDefinitionInput = (
  form: TriggerFormState,
  existing?: TriggerDefinitionFieldsFragment | null
): CreateTriggerDefinitionInput => ({
  name: form.name.trim(),
  eventMatcher: buildEventMatcher(form, existing),
  target: {
    type: TriggerTargetType.WorkspaceRun,
    ...targetFromTask(form.task),
    prompt: form.prompt.trim() || undefined
  }
});
